import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Observable } from "rxjs";

import {
  RecordingFeeEstimateBatchRequest,
  RecordingFeeEstimateBatchResult,
  RecordingFeeEstimateRequest,
  RecordingFeeEstimateResult,
} from "@juristech/contracts/payments/recording-fee.types";

/**
 * Endpoint for recording-fee estimates. Rides the `/nest-api` SSR proxy so the
 * backend infers the tenant from the request Host header; the endpoint is
 * public (no credentials required).
 */
export const RECORDING_FEE_ESTIMATE_ENDPOINT =
  "/nest-api/recording-fees/estimate";

/**
 * Batch variant of {@link RECORDING_FEE_ESTIMATE_ENDPOINT} (plan decision 10)
 * — up to 50 per-document estimate requests in ONE call, index-aligned
 * results. Used by multi-document forms (USDR) that would otherwise trip the
 * estimate endpoint's per-minute throttle with N per-row calls.
 */
export const RECORDING_FEE_ESTIMATE_BATCH_ENDPOINT =
  "/nest-api/recording-fees/estimate-batch";

/**
 * Fetches recording-fee estimates from the NestJS `/recording-fees/estimate`
 * (and batch sibling) endpoints via the `/nest-api` SSR proxy (tenant
 * resolved from Host).
 */
@Injectable({ providedIn: "root" })
export class RecordingFeeEstimateService {
  private readonly http = inject(HttpClient);

  /**
   * Requests a recording-fee estimate for the given location and answers.
   *
   * @param request - Location ids, optional consideration amount, and any
   *   answers to the calc engine's per-office questions.
   * @returns Observable emitting the estimate result (availability, total in
   *   cents, and any outstanding questions).
   */
  estimate(
    request: RecordingFeeEstimateRequest,
  ): Observable<RecordingFeeEstimateResult> {
    return this.http.post<RecordingFeeEstimateResult>(
      RECORDING_FEE_ESTIMATE_ENDPOINT,
      request,
    );
  }

  /**
   * Requests recording-fee estimates for up to 50 documents in one call
   * (plan decision 10). Results are strictly index-aligned with
   * `request.requests`.
   *
   * @param request - Batch of per-document estimate inputs plus the optional
   *   `currentHostname` / `domainCompanyId` tenant hints (USDR sends these;
   *   web's single-shot `estimate` call does not, since web rides its own
   *   SSR proxy's Host header).
   * @returns Observable emitting the index-aligned batch result.
   */
  estimateBatch(
    request: RecordingFeeEstimateBatchRequest,
  ): Observable<RecordingFeeEstimateBatchResult> {
    return this.http.post<RecordingFeeEstimateBatchResult>(
      RECORDING_FEE_ESTIMATE_BATCH_ENDPOINT,
      request,
    );
  }
}
